import React, { useState, useCallback, useContext } from 'react';
import { Button, Spin, message } from 'antd';
import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '@services/api';
import SessionContext from '@providers/Session/SessionContext';
import { useNavigate } from 'react-router-dom';

interface ICsvDetailedInvoice {
  customerId: string | undefined;
  invoiceId: string | undefined;
}

const CsvDetailedInvoice = ({ customerId, invoiceId }: ICsvDetailedInvoice) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['invoices', 'reports']);
  const { userLogged } = useContext(SessionContext);

  const userLanguage = userLogged?.attributes?.locale ?? 'pt';

  const [downloadProgress, setDownloadProgress] = useState(false);

  const getInvoiceData = useCallback(
    () =>
      api.get(
        `/service-proxy/broker/${customerId}/invoices/${invoiceId}/detailed?locale=${userLanguage}`,
      ),
    [customerId, invoiceId, userLanguage],
  );

  const navigateToReports = useCallback(() => {
    message.destroy('report-requested');
    navigate(`/support/reports?type=broker_invoice`);
  }, [navigate]);

  const handleGetDetailedInvoice = useCallback(async () => {
    setDownloadProgress(true);
    try {
      await getInvoiceData();
      message.success({
        content: (
          <span>
            {t('reports:request.report-success')}
            <Button
              onClick={navigateToReports}
              type="link"
              style={{ padding: 3 }}
            >
              {t('reports:request.go-to-reports')}
            </Button>
            {t('reports:request.check-report-progress-and-download')}
          </span>
        ),
        key: 'report-requested',
        duration: 5,
      });
    } catch (e) {
      message.error(t('errors.exporting'));
    }
    setDownloadProgress(false);
  }, [getInvoiceData, t, navigateToReports]);

  return (
    <Button
      type="primary"
      icon={<DownloadOutlined />}
      onClick={handleGetDetailedInvoice}
    >
      {downloadProgress ? (
        <Spin
          indicator={
            <LoadingOutlined style={{ color: 'white', marginLeft: 10 }} spin />
          }
        />
      ) : (
        t('csvExport')
      )}
    </Button>
  );
};

export default CsvDetailedInvoice;
